import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlug, FaMapMarkerAlt, FaHistory, FaInfoCircle } from 'react-icons/fa';
import { KavachSwitch } from '../utils/kavachTypes';
import { getSwitchHealth } from '../utils/kavachConstants';

interface KavachSwitchCardsProps {
  switches: KavachSwitch[];
  onSwitchClick: (sw: KavachSwitch) => void;
}

const KavachSwitchCards: React.FC<KavachSwitchCardsProps> = ({ switches, onSwitchClick }) => {
  const navigate = useNavigate();

  const getStatusBadgeClass = (health: 'healthy' | 'partial' | 'critical') => {
    switch (health) {
      case 'healthy':
        return 'status-all-up';
      case 'partial':
        return 'status-partial';
      case 'critical':
        return 'status-all-down';
    }
  };

  const getStatusLabel = (health: 'healthy' | 'partial' | 'critical') => {
    switch (health) {
      case 'healthy':
        return 'All Up';
      case 'partial':
        return 'Partial';
      case 'critical':
        return 'Critical';
    }
  };

  const getUpCount = (sw: KavachSwitch) => {
    return Object.values(sw.ports).filter((p) => p.status === 'UP').length;
  };

  const handleViewDetails = (e: React.MouseEvent, sw: KavachSwitch) => {
    e.stopPropagation();
    navigate(`/kavach-switch-detail?deviceId=${sw.id}&deviceName=${sw.name}`);
  };

  const handleViewHistory = (e: React.MouseEvent, sw: KavachSwitch) => {
    e.stopPropagation();
    navigate(`/kavach-switch-history/${sw.id}`);
  };

  return (
    <div className="cards-container">
      {switches.map((sw) => {
        const health = getSwitchHealth(sw);
        const ports = [
          { name: 'Port 1', ...sw.ports.port1 },
          { name: 'Port 2', ...sw.ports.port2 },
          { name: 'Port 3', ...sw.ports.port3 },
          { name: 'Port 4', ...sw.ports.port4 },
        ];

        return (
          <div key={sw.id} className="switch-card" onClick={() => onSwitchClick(sw)}>
            <div className="switch-card-header">
              <div className="switch-info">
                <h3>{sw.name.length > 25 ? `${sw.name.substring(0, 25)}...` : sw.name}</h3>
                <div className="switch-location">
                  <FaMapMarkerAlt />
                  {sw.station}
                </div>
              </div>
              <span className={`switch-status-badge ${getStatusBadgeClass(health)}`}>
                {getStatusLabel(health)}
              </span>
            </div>

            <div className="switch-card-body">
              {ports.map((port, index) => (
                <div key={index} className="port-row">
                  <div className="port-info">
                    <div className={`port-icon ${port.status.toLowerCase()}`}>
                      <FaPlug />
                    </div>
                    <div className="port-details">
                      <h4>{port.name}</h4>
                      <span className="port-status-text">{port.status}</span>
                    </div>
                  </div>
                  <span className="port-speed">{port.speed}</span>
                </div>
              ))}
            </div>

            <div className="switch-card-footer">
              <span>Last: {sw.lastActivity}</span>
              <span>{getUpCount(sw)}/4 ports up</span>
            </div>

            <div className="switch-card-actions">
              <button
                className="card-action-btn"
                onClick={(e) => handleViewDetails(e, sw)}
                title="View Details"
              >
                <FaInfoCircle /> Details
              </button>
              <button
                className="card-action-btn"
                onClick={(e) => handleViewHistory(e, sw)}
                title="View History"
              >
                <FaHistory /> History
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KavachSwitchCards;
